import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { IMAGES, TESTIMONIALS } from "@/lib/constants";
import {
  ShieldCheck, Leaf, FlaskConical, MapPin, FileText,
  ArrowRight, Instagram, Twitter, Facebook, Send, CheckCircle,
  Save, RefreshCw, Share2, BookOpen,
} from "lucide-react";

const scrollToStudio = () => {
  const el = document.getElementById("customization-studio");
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

/* ====================== NUTRITION / TRUST ====================== */
const trustItems = [
  { icon: FlaskConical, title: "Nutritional Estimation", desc: "See estimated sodium, calories, and macros before you order." },
  { icon: Leaf, title: "Ingredient Transparency", desc: "Every ingredient listed clearly. No hidden additives." },
  { icon: MapPin, title: "Andhra Origin", desc: "Sourced from authentic Andhra Pradesh spice regions." },
  { icon: FileText, title: "Profile Labeling", desc: "Every order labeled by its smart profile and customizations." },
];

export const NutritionTrust = () => (
  <section data-testid="nutrition-trust-section" className="py-24 lg:py-36 bg-cream-secondary/40">
    <div className="section-container">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.8 }}
        className="text-center max-w-2xl mx-auto mb-16"
      >
        <span className="text-sm font-medium text-earth-green uppercase tracking-wider">Transparency</span>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-700 text-sw-text mt-4 leading-tight">
          Every customization comes with{" "}
          <span className="font-accent italic text-earth-green">clarity</span>
        </h2>
      </motion.div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
        {trustItems.map((item, i) => (
          <motion.div
            key={item.title}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: i * 0.1 }}
            data-testid={`trust-item-${i}`}
            className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm"
          >
            <div className="w-11 h-11 rounded-xl bg-earth-green/10 flex items-center justify-center mb-4">
              <item.icon size={20} className="text-earth-green" />
            </div>
            <h3 className="font-heading font-600 text-sw-text mb-2">{item.title}</h3>
            <p className="text-sm text-sw-text-secondary leading-relaxed">{item.desc}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

/* ====================== MY RECIPES ====================== */
const recipeFeatures = [
  { icon: Save, title: "Save Custom Recipes", desc: "Your personalized blends, saved for quick reordering." },
  { icon: RefreshCw, title: "Tweak & Reorder", desc: "Adjust any recipe and reorder with one click." },
  { icon: Share2, title: "Share Creations", desc: "Share your custom recipes with friends and family." },
];

export const MyRecipes = () => (
  <section id="my-recipes" data-testid="my-recipes-section" className="py-24 lg:py-36">
    <div className="section-container">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <span className="text-sm font-medium text-earth-green uppercase tracking-wider">My Recipes</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-700 text-sw-text mt-4 leading-tight">
            Your taste, <span className="font-accent italic text-earth-green">saved forever</span>
          </h2>
          <p className="text-base text-sw-text-secondary mt-4 leading-relaxed max-w-md">
            Build your personal cookbook of custom Indian flavors. Save recipes, tweak them, share them, and reorder in one click.
          </p>

          <div className="space-y-5 mt-8">
            {recipeFeatures.map((feat, i) => (
              <motion.div
                key={feat.title}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.15 }}
                className="flex gap-4"
              >
                <div className="w-10 h-10 rounded-xl bg-earth-green/10 flex items-center justify-center shrink-0">
                  <feat.icon size={18} className="text-earth-green" />
                </div>
                <div>
                  <h4 className="font-heading font-600 text-sw-text text-sm">{feat.title}</h4>
                  <p className="text-sm text-sw-text-secondary">{feat.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="relative"
        >
          <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-sm">
            <div className="flex items-center gap-3 mb-5">
              <BookOpen size={20} className="text-earth-green" />
              <h3 className="font-heading font-700 text-sw-text">My Saved Recipes</h3>
            </div>
            {[
              { name: "Mild Mango Pickle", profile: "Kid-Friendly", date: "Saved 2 days ago" },
              { name: "Low-Salt Avakaya", profile: "Heart-Healthy", date: "Saved 1 week ago" },
              { name: "Fiery Chilli Pickle", profile: "Custom", date: "Saved 3 weeks ago" },
            ].map((recipe, i) => (
              <div key={i} className={`flex items-center justify-between py-4 ${i < 2 ? "border-b border-gray-100" : ""}`}>
                <div>
                  <h4 className="font-heading font-600 text-sm text-sw-text">{recipe.name}</h4>
                  <p className="text-xs text-sw-text-muted">{recipe.profile} &middot; {recipe.date}</p>
                </div>
                <button className="text-xs text-earth-green font-medium hover:underline">Reorder</button>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

/* ====================== BRAND STORY ====================== */
export const BrandStory = () => (
  <section id="brand-story" data-testid="brand-story-section" className="py-24 lg:py-36 bg-cream-secondary/40 overflow-hidden">
    <div className="section-container">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="relative rounded-3xl overflow-hidden aspect-[4/5] lg:aspect-auto lg:h-[600px]"
        >
          <img
            src={IMAGES.grandmaCooking}
            alt="Traditional Indian cooking"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-sw-text/40 to-transparent" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="space-y-6"
        >
          <span className="text-sm font-medium text-earth-green uppercase tracking-wider">Our Story</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-700 text-sw-text leading-tight">
            Where tradition meets{" "}
            <span className="font-accent italic text-earth-green">intention</span>
          </h2>
          <div className="space-y-4 text-base text-sw-text-secondary leading-relaxed">
            <p>
              Traditional food has always been personal. Every household adjusts recipes
              with care &#8212; for age, for preference, for mood, and for health.
            </p>
            <p>
              Swadishes is inspired by that very instinct. The instinct of a mother
              reducing spice for her child. A partner swapping ingredients for a loved
              one's health. A grandmother adjusting flavors with decades of intuition.
            </p>
            <p className="font-accent italic text-earth-green text-lg">
              We bring that same care into a modern platform &#8212; so every jar feels
              like it was made just for you.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

/* ====================== TESTIMONIALS ====================== */
export const Testimonials = () => (
  <section data-testid="testimonials-section" className="py-24 lg:py-36">
    <div className="section-container">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        className="text-center max-w-2xl mx-auto mb-16"
      >
        <span className="text-sm font-medium text-earth-green uppercase tracking-wider">What People Say</span>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-700 text-sw-text mt-4 leading-tight">
          Voices from the{" "}
          <span className="font-accent italic text-earth-green">Swadishes family</span>
        </h2>
      </motion.div>

      <div className="grid sm:grid-cols-2 gap-6 max-w-4xl mx-auto">
        {TESTIMONIALS.map((t, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: i * 0.1 }}
            data-testid={`testimonial-${i}`}
            className="bg-white rounded-2xl p-6 lg:p-8 border border-gray-100 shadow-sm"
          >
            <div className="testimonial-quote">
              <p className="text-base text-sw-text leading-relaxed pl-4">{t.quote}</p>
            </div>
            <div className="mt-5 pt-4 border-t border-gray-100 flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-earth-green/10 flex items-center justify-center">
                <span className="text-earth-green font-heading font-700 text-sm">{t.author[0]}</span>
              </div>
              <div>
                <p className="font-heading font-600 text-sm text-sw-text">{t.author}</p>
                <p className="text-xs text-sw-text-muted">{t.location}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

/* ====================== FINAL CTA ====================== */
export const FinalCTA = () => {
  const navigate = useNavigate();
  return (
    <section data-testid="final-cta-section" className="py-24 lg:py-36 bg-cream-secondary/60">
      <div className="section-container text-center">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-2xl mx-auto"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-6xl font-heading font-800 text-sw-text leading-tight">
            Make tradition{" "}
            <span className="font-accent italic text-earth-green">truly yours.</span>
          </h2>
          <p className="text-base lg:text-lg text-sw-text-secondary mt-6 leading-relaxed max-w-lg mx-auto">
            Personalized Indian pickles, crafted around your taste and wellness needs.
          </p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-8"
          >
            <button
              data-testid="final-cta-btn"
              onClick={() => navigate("/studio")}
              className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-earth-green px-10 py-4 text-base font-medium text-white shadow-lg shadow-earth-green/20 transition-all duration-300 hover:bg-earth-green-dark hover:shadow-xl hover:shadow-earth-green/30 sm:w-auto"
            >
              Start Customizing
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

/* ====================== CONTACT ====================== */
export const ContactSection = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await new Promise((r) => setTimeout(r, 900));
    setLoading(false);
    setSubmitted(true);
  };

  const inputCls = "w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-sw-text placeholder:text-sw-text-muted focus:border-earth-green focus:outline-none focus:ring-2 focus:ring-earth-green/15 transition-all";

  return (
    <section id="contact" className="py-24 lg:py-32 bg-cream-secondary/40">
      <div className="section-container">
        <div className="grid lg:grid-cols-2 gap-16 items-start max-w-5xl mx-auto">

          {/* Left */}
          <motion.div
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.7 }}
          >
            <span className="text-xs font-medium text-earth-green uppercase tracking-widest mb-4 block">Get in touch</span>
            <h2 className="font-heading font-bold text-3xl lg:text-4xl text-sw-text leading-tight mb-5">
              We would love<br />
              <span className="font-accent italic text-earth-green">to hear from you.</span>
            </h2>
            <p className="text-sw-text-secondary text-base leading-relaxed mb-8">
              Whether you have a question about your order, want to know more about
              how we work, or simply want to share feedback — we read every message
              and respond personally.
            </p>
            <div className="space-y-4">
              {[
                { label: "For orders and delivery", value: "orders@swadishes.com" },
                { label: "For everything else", value: "hello@swadishes.com" },
                { label: "Based in", value: "Bhavapuri, AP · Amravathi, MH" },
              ].map(({ label, value }) => (
                <div key={label} className="flex flex-col">
                  <span className="text-xs text-sw-text-muted uppercase tracking-wider">{label}</span>
                  <span className="text-sm font-medium text-sw-text mt-0.5">{value}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.1 }}
          >
            {submitted ? (
              <div className="flex flex-col items-center justify-center text-center bg-white rounded-3xl border border-gray-100 shadow-sm p-12">
                <div className="w-14 h-14 rounded-full bg-earth-green/10 flex items-center justify-center mb-5">
                  <CheckCircle size={26} className="text-earth-green" />
                </div>
                <h3 className="font-heading font-bold text-xl text-sw-text mb-2">Message received</h3>
                <p className="text-sm text-sw-text-secondary leading-relaxed max-w-xs">
                  Thank you for writing in. We will get back to you within 24 hours.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="bg-white rounded-3xl border border-gray-100 shadow-sm p-8 space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-sw-text-muted uppercase tracking-wider mb-1.5 block">Name</label>
                    <input name="name" required value={form.name} onChange={handle} placeholder="Your name" className={inputCls} />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-sw-text-muted uppercase tracking-wider mb-1.5 block">Email</label>
                    <input name="email" type="email" required value={form.email} onChange={handle} placeholder="you@example.com" className={inputCls} />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-sw-text-muted uppercase tracking-wider mb-1.5 block">Subject</label>
                  <select name="subject" required value={form.subject} onChange={handle} className={inputCls}>
                    <option value="" disabled>Select a topic</option>
                    <option>Order or delivery question</option>
                    <option>Customization help</option>
                    <option>Product feedback</option>
                    <option>Bulk or gifting enquiry</option>
                    <option>Something else</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-sw-text-muted uppercase tracking-wider mb-1.5 block">Message</label>
                  <textarea name="message" required rows={4} value={form.message} onChange={handle} placeholder="Tell us what's on your mind..." className={`${inputCls} resize-none`} />
                </div>
                <button
                  type="submit" disabled={loading}
                  className="w-full flex items-center justify-center gap-2 bg-earth-green text-white rounded-xl py-3.5 text-sm font-medium hover:bg-earth-green-dark transition-all duration-300 shadow-md shadow-earth-green/20 disabled:opacity-70"
                >
                  {loading ? (
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <><Send size={14} /> Send message</>
                  )}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

/* ====================== FOOTER ====================== */
const footerLinks = {
  Shop: [
    { label: "All Products", href: "/#products" },
    { label: "Signature Pickles", href: "/#products" },
    { label: "Health Collections", href: "/#products" },
    { label: "Custom Pickles", href: "/studio" },
  ],
  Discover: [
    { label: "How It Works", href: "/#how-it-works" },
    { label: "Smart Profiles", href: "/#smart-profiles" },
    { label: "Customization Studio", href: "/studio" },
  ],
  Company: [
    { label: "About Us", href: "/about" },
    { label: "Contact", href: "/#contact" },
    { label: "Careers", href: "#" },
  ],
  Support: [
    { label: "FAQs", href: "#" },
    { label: "Shipping Info", href: "#" },
    { label: "Privacy Policy", href: "#" },
    { label: "Terms of Service", href: "#" },
  ],
};

export const Footer = () => {
  const navigate = useNavigate();

  const handleLink = (e, href) => {
    e.preventDefault();
    if (href === "#") return;
    if (href.startsWith("/") && !href.startsWith("/#")) {
      navigate(href);
    } else if (href.startsWith("/#")) {
      const id = href.replace("/#", "");
      if (window.location.pathname === "/") {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      } else {
        navigate("/");
        setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 400);
      }
    }
  };

  return (
    <footer data-testid="footer" className="bg-sw-text text-white py-16 lg:py-20">
      <div className="section-container">
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-5 lg:gap-12">
          <div className="sm:col-span-2 lg:col-span-1">
            <img src={IMAGES.logo} alt="Swadishes" className="h-12 rounded-md mb-4" />
            <p className="text-sm text-gray-400 leading-relaxed max-w-xs">
              Craft Your Taste. Designed for Your Health.
            </p>
            <div className="flex gap-4 mt-6">
              {[Instagram, Twitter, Facebook].map((Icon, i) => (
                <a key={i} href="#" data-testid={`social-link-${i}`}
                  className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-earth-green transition-colors duration-300">
                  <Icon size={16} />
                </a>
              ))}
            </div>
          </div>

          {Object.entries(footerLinks).map(([heading, links]) => (
            <div key={heading}>
              <h4 className="font-heading font-semibold text-sm mb-4">{heading}</h4>
              <ul className="space-y-2.5">
                {links.map(({ label, href }) => (
                  <li key={label}>
                    <a href={href} onClick={(e) => handleLink(e, href)}
                      className={`text-sm transition-colors duration-300 ${href === "#" ? "text-gray-600 cursor-default" : "text-gray-400 hover:text-white cursor-pointer"}`}>
                      {label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="border-t border-white/10 mt-12 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-xs text-gray-500">&copy; 2026 Swadishes. All rights reserved.</p>
          <p className="text-xs text-gray-500">Made with care, in Andhra Pradesh.</p>
        </div>
      </div>
    </footer>
  );
};
